"use client";

import type {
  CustomerServiceFeedbackErrorKind,
  CustomerServiceFormData,
} from "./customer-service-form.schema";

import { useMutation } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { submitCustomerServiceFeedback } from "~www/actions/submit-customer-service-feedback";
import { trackCustomerServiceFormSubmit } from "./track-customer-service-form-submit";

class CustomerServiceSubmitError extends Error {
  result: CustomerServiceFeedbackErrorKind;

  constructor(message: string, result: CustomerServiceFeedbackErrorKind) {
    super(message);
    this.result = result;
  }
}

/**
 * Submits customer service feedback via a server action.
 *
 * @throws {CustomerServiceSubmitError} `"validation-error"` — server-side
 *   validation failed.
 * @throws {CustomerServiceSubmitError} `"server-error"` — Azure submission
 *   failed with a CRM/campaign-style message.
 * @throws {CustomerServiceSubmitError} `"exception-error"` — guard failures,
 *   network issues, or other unexpected errors.
 */
async function postCustomerServiceFeedback(
  data: CustomerServiceFormData,
): Promise<void> {
  const result = await submitCustomerServiceFeedback(data);

  if (result.ok) {
    return;
  }

  const message = result.error ?? "Submission failed. Please try again.";
  throw new CustomerServiceSubmitError(
    message,
    result.errorKind ?? "exception-error",
  );
}

function useSubmitCustomerServiceFeedback(thankYouPageUrl: string) {
  const router = useRouter();

  return useMutation({
    mutationFn: postCustomerServiceFeedback,
    onError: (error, variables) => {
      const result =
        error instanceof CustomerServiceSubmitError
          ? error.result
          : "exception-error";
      trackCustomerServiceFormSubmit(result, variables.franchise);
    },
    onSuccess: (_data, variables) => {
      trackCustomerServiceFormSubmit("success", variables.franchise);
      router.push(thankYouPageUrl ?? `${window.location.pathname}thank-you/`);
    },
  });
}

export { useSubmitCustomerServiceFeedback };
